import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Case } from "../../types/case";
import { deleteCase, fetchCases, patchCaseData } from "../../services/httpClient";
import { Button, Card, Col, Container, Dropdown, DropdownButton, InputGroup, Row, Spinner, Stack } from "react-bootstrap";
import SearchBar from "../ui/SearchBar";
import PageTitle from "../ui/PageTitle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose, faRepeat } from "@fortawesome/free-solid-svg-icons";
import { Tooltip } from "react-tooltip";
import SelectObjectTypeahead from "../ui/SelectObjectTypeahead";

interface CaseTabState {
  cases: Case[];
  setCases: any;
  casesFiltered: Case[];
  setCasesFiltered: any;
  cmUsers: any[];
  type: string;
  getCases: any;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
  limitedAccess: boolean;
}

const CaseTab = ({
  cases,
  setCases,
  casesFiltered,
  setCasesFiltered,
  cmUsers,
  type,
  getCases,
  setShowToast,
  setToastText,
  setToastBody,
  limitedAccess,
}: CaseTabState) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [caseToDelete, setCaseToDelete] = useState<Case>();
  const [searchText, setSearchText] = useState<string>("");
  const [caseFilter, setCaseFilter] = useState<string>("");
  const [filteredCm, setFilteredCm] = useState<any>();
  const [selectedReassignCases, setSelectedReassignCases] = useState<any[]>([]);
  const [isReassign, setIsReassign] = useState<boolean>(false);
  const [isConfirmReassign, setIsConfirmReassign] = useState<boolean>(false);
  const [reassignCm, setReassignCm] = useState<any>();
  const [errMsg, setErrMsg] = useState<string>("");

  const searchRef = useRef<HTMLInputElement>(null);

  let navigate = useNavigate();

  const newCase = (e: any) => {
    e.preventDefault();
    navigate(`/cases/case/create/${type === "All" ? "Active" : type}`);
  };

  const editCase = (e: any) => {
    navigate(`/cases/case/${e}`);
  };

  const onClickDelete = (e: any) => {
    setIsDeleting(true);
    setCaseToDelete(e);
  };

  const onClickDeleteFinal = () => {
    setIsLoading(true);
    setIsDeleting(false);
    const caseName: string = caseToDelete?.name ? caseToDelete.name : "";

    deleteCase(caseToDelete?.id)
      .then((res: any) => {
        setIsDeleting(false);
        setCaseToDelete(undefined);
        fetchCases()
          .then((res: any) => {
            const cases = res[0].data;
            setCases(cases);
            setShowToast(true);
            setToastText("Case Deleted");
            setToastBody(caseName);
            setIsLoading(false);
          })
          .catch((err: any) => {
            console.log(err);
            setIsLoading(false);
          });
      })
      .catch((err: any) => {
        console.log(err);
        setIsLoading(false);
      });
  };

  const onClickCancelDelete = () => {
    setIsDeleting(false);
    setCaseToDelete(undefined);
  };

  const onPerformSearchLogic = (text: string) => {
    clearFilters();
    const lowerText = text.toLowerCase();

    var myFiltered: any;
    var myCases = cases;

    //filter by name of case
    myFiltered = myCases?.filter((item: any) => {
      return item.name.toLowerCase().match(lowerText);
    });

    setCasesFiltered(myFiltered);
  };

  const onClickFilterCases = (type: string) => {
    setSearchText("");

    if (type === caseFilter) {
      clearFilters();
      return;
    }
    setCaseFilter(type);
  };

  const onClickFilterBy = (values: any) => {
    var selected: any;
    var filtered;
    var cmIDs: any = [];

    setFilteredCm(values);
    selected = cmUsers.filter((c) => values?.key === c.key);
    if (selected.length > 0) {
      setSearchText("");

      cmIDs = [];
      selected.forEach((selectedCM: any) => {
        cmIDs.push(selectedCM.id);
      });
    }

    switch (caseFilter) {
      case "primaryCM":
        if (selected.length > 0) {
          filtered = cases.filter((c: any) => cmIDs.includes(c.primaryCM?.user_id));
          if (filtered.length > 0) {
            filtered = filtered.map((f: any) => ({
              ...f,
              selected: false,
            }));
          }

          setCasesFiltered(filtered);
        } else {
          clearFilters();
        }
        break;
      case "secondaryCM":
        if (selected.length > 0) {
          filtered = cases.filter((c: any) => cmIDs.includes(c.secondaryCM?.user_id));
          // if (filtered.length > 0) {
          //   setIsReassignDisabled(false);
          // }
          // else {
          //   setIsReassignDisabled(true);
          // }
          setCasesFiltered(filtered);
        } else {
          clearFilters();
        }
        break;
    }

    if (!isReassign) {
      toggleReassign();
    }
  };

  useEffect(() => {
    if (caseFilter && filteredCm) {
      onClickFilterBy(filteredCm);
    }
  }, [caseFilter]);

  const onClickCaseToReassign = (c: any) => {
    if (isConfirmReassign || !reassignCm) return;

    const invalidPrimaryCM = caseFilter === "primaryCM" && c.secondaryCM?.user_id === reassignCm?.id;
    const invalidSecondaryCM = caseFilter === "secondaryCM" && c.primaryCM?.user_id === reassignCm?.id;

    if (invalidPrimaryCM) {
      setErrMsg(`${reassignCm?.name} is currently assigned as Secondary Case Manager`);
      return;
    }

    if (invalidSecondaryCM) {
      setErrMsg(`${reassignCm?.name} is currently assigned as Primary Case Manager`);
      return;
    }

    setErrMsg("");

    const updated = { ...c, selected: !c.selected };

    if (!c.selected) {
      setSelectedReassignCases((prev) => [...prev, updated]);
    } else {
      setSelectedReassignCases((prev) => prev.filter((item: any) => item?.id !== updated.id));
    }

    setCasesFiltered((prev: any) => prev.map((item: any) => (item.id === c?.id ? { ...item, selected: !c.selected } : item)));
  };

  const clearFilters = () => {
    setCaseFilter("");
    setFilteredCm(undefined);
    setCasesFiltered(cases);
    setIsReassign(false);
    setIsConfirmReassign(false);
    setReassignCm(undefined);
    setSelectedReassignCases([]);
    setErrMsg("");
  };

  const updateReassignCM = (e: any) => {
    setReassignCm(e);
  };

  //prevent cases staying selected when cancelling
  const toggleReassign = () => {
    if (isReassign) {
      setSelectedReassignCases([]);
      setCasesFiltered((prev: any) => (prev ? prev.map((p: any) => ({ ...p, selected: false })) : prev));
      setReassignCm(undefined);
      setIsConfirmReassign(false);
      setIsReassign(false);
      setErrMsg("");
    } else {
      setIsReassign(true);
    }
  };

  const onClickReassignFinal = () => {
    setIsLoading(true);
    selectedReassignCases.forEach(async (caseItem: any) => {
      await reassignCaseManager(caseItem);
    });

    setTimeout(() => {
      getCases();
    }, 2000);

    clearFilters();
    setShowToast(true);
    setToastText("Cases Reassigned");
    setToastBody(selectedReassignCases.length + " case(s)");
  };

  const reassignCaseManager = async (caseItem: any) => {
    var updatedCase: any = {};
    if (caseFilter === "primaryCM") {
      updatedCase.primaryCM = reassignCm?.id;
    } else {
      updatedCase.secondaryCM = reassignCm?.id;
    }

    patchCaseData(updatedCase, caseItem.id).catch((err: any) => {
      console.log(err);
    });
  };

  const onClickReassignCancel = () => {
    clearFilters();
  };

  const categorizeCases = (cases: Case[]) => {
    const categorized = {
      Active: cases.filter((c) => c.casestatus === "active"),
      Waitlist: cases.filter((c) => c.casestatus === "waitlist"),
      Inactive: cases.filter((c) => c.casestatus === "inactive"),
    };
    return categorized;
  };

  const renderCaseCategory = (categoryName: string, categoryItems: Case[]) => {
    if (categoryItems.length === 0) return null;

    return (
      <div key={categoryName} className="mb-4">
        <h6 className="app-orange-text mb-3">{categoryName} Cases ({categoryItems.length})</h6>
        <Stack gap={2}>
          {categoryItems.map((item: any) => (
            <Card key={item?.id} className={item.selected ? "border-tertiary" : "bg-light"}>
              <Card.Body>
                <Stack direction="horizontal" gap={3}>
                  <Stack
                    className={reassignCm ? "cursor-pointer" : ""}
                    onClick={isReassign ? () => onClickCaseToReassign(item) : (e) => editCase(item.id)}
                  >
                    {item.disabled_at ? (
                      <Card.Subtitle className="text-muted">{item.name}</Card.Subtitle>
                    ) : (
                      <Card.Subtitle>{item.name}</Card.Subtitle>
                    )}
                    <Card.Subtitle className="text-muted mt-1">
                      {caseFilter === "primaryCM" && item.primaryCM !== null && `Primary Case Manager`}
                      {caseFilter === "secondaryCM" && item.secondaryCM !== null && ` Secondary Case Manager`}
                    </Card.Subtitle>
                    <Card.Subtitle className="text-muted mt-1">
                      {item.primaryCM === null && filteredCm && "Primary Case Manager Unassigned"}
                    </Card.Subtitle>
                  </Stack>
                  {!isReassign && (
                    <>
                      <Button className="app-secondary-color button" onClick={(e) => editCase(item.id)}>
                        Edit
                      </Button>
                      {!limitedAccess && (
                        <>
                          <div className="vr" />
                          <Button className="app-danger-color button" onClick={(e) => onClickDelete(item)}>
                            Delete
                          </Button>
                        </>
                      )}
                    </>
                  )}
                </Stack>
              </Card.Body>
            </Card>
          ))}
        </Stack>
      </div>
    );
  };

  const renderFilterBy = () => {
    let cmUsersWithCases = cmUsers;
    
    if (caseFilter === "primaryCM") {
      const assignedIds = new Set(cases.map((c: any) => c.primaryCM?.user_id).filter(Boolean));
      cmUsersWithCases = cmUsers.filter((cm: any) => assignedIds.has(cm.id));
    } else if (caseFilter === "secondaryCM") {
      const assignedIds = new Set(cases.map((c: any) => c.secondaryCM?.user_id).filter(Boolean));
      cmUsersWithCases = cmUsers.filter((cm: any) => assignedIds.has(cm.id));
    }

    return (
      <Row className={`${!isReassign && "mb-3"} justify-content-center align-items-center`}>
        <Col xs={1}>
          <h6 className="app-header-text m-0">From:</h6>
        </Col>
        <Col xs={4}>
          <SelectObjectTypeahead
            label={caseFilter === "primaryCM" ? "Primary Case Manager" : "Secondary Case Manager"}
            value={filteredCm}
            options={cmUsersWithCases}
            onUpdate={(e: any) => onClickFilterBy(e)}
            focused={true}
            cssClasses="w-100"
          />
        </Col>
        <Col xs={1}></Col>
      </Row>
    );
  };

  const renderConfirmReassign = () => {
    return (
      <Stack direction="horizontal" gap={3}>
        <Stack style={{ justifyContent: "center" }}>
          <Card.Subtitle>
            Reassign {caseFilter === "primaryCM" ? "Primary Case Manager" : "Secondary Case Manager"} for {selectedReassignCases.length}{" "}
            case(s) from {filteredCm?.name} to {reassignCm?.name}
          </Card.Subtitle>
        </Stack>
        <Button className="app-tertiary-color button" disabled={reassignCm ? false : true} onClick={() => onClickReassignFinal()}>
          Confirm Reassign
        </Button>
        <div className="vr" />
        <Button className="app-primary-color button" onClick={() => onClickReassignCancel()}>
          Cancel
        </Button>
      </Stack>
    );
  };

  const renderDelete = () => {
    return (
      <Card className="bg-light">
        <Card.Body>
          <Stack direction="horizontal" gap={3}>
            <Stack style={{ justifyContent: "center" }}>
              <Card.Subtitle>Are you sure you want to delete {caseToDelete?.name}?</Card.Subtitle>
            </Stack>
            <Button className="app-danger-color button" onClick={() => onClickDeleteFinal()}>
              Confirm Delete
            </Button>
            <div className="vr" />
            <Button className="app-primary-color button" onClick={() => onClickCancelDelete()}>
              Cancel
            </Button>
          </Stack>
        </Card.Body>
      </Card>
    );
  };

  return (
    <Row className="mb-5">
      <Col>
        <Stack gap={3}>
          {isLoading ? (
            <Container className="div-main">
              <Button size="sm" className="spinner-button spinner-button-main">
                <Spinner animation="border" style={{ color: "#F6893D" }} />
              </Button>
            </Container>
          ) : (
            <>
              <PageTitle
                title={`${caseFilter ? "Reassign " : ""}${type} Cases`}
                type="section"
                buttonTitle={caseFilter ? "Cancel" : "Create Case"}
                onButtonClick={isConfirmReassign ? null : caseFilter ? onClickReassignCancel : newCase}
              />
              <Row className="mb-1">
                <Col xs={12}>
                  {!caseFilter && (
                    <InputGroup>
                      <Col>
                        <SearchBar
                          cssClass="search"
                          placeholder={"Search " + type + " Cases"}
                          searchText={searchText}
                          setSearchText={setSearchText}
                          searchLogic={onPerformSearchLogic}
                          externalRef={searchRef}
                        />
                      </Col>
                      <div data-tooltip-id="tooltip-filter" data-tooltip-content={casesFiltered.length === 0 ? "No Cases to Reassign" : "Reassign"}>
                        <DropdownButton
                          id={caseFilter ? "dropdown-filter-button-active" : "dropdown-filter-button"}
                          title={<FontAwesomeIcon icon={faRepeat} />}
                          disabled={casesFiltered.length === 0}
                        >
                          <Dropdown.Item
                            href="#/action-1"
                            className={caseFilter === "primaryCM" ? "app-tertiary-color-inverse" : ""}
                            onClick={() => onClickFilterCases("primaryCM")}
                          >
                            Primary Case Manager
                            {caseFilter === "primaryCM" ? <FontAwesomeIcon icon={faClose} className="ms-2" /> : ""}
                          </Dropdown.Item>
                          <Dropdown.Item
                            href="#/action-2"
                            className={caseFilter === "secondaryCM" ? "app-tertiary-color-inverse" : ""}
                            onClick={() => onClickFilterCases("secondaryCM")}
                          >
                            Secondary Case Manager
                            {caseFilter === "secondaryCM" ? <FontAwesomeIcon icon={faClose} className="ms-2" /> : ""}
                          </Dropdown.Item>
                        </DropdownButton>
                      </div>
                      <Tooltip id="tooltip-filter" place="top" style={{ padding: "5px 10px", zIndex: "100" }} />
                    </InputGroup>
                  )}
                </Col>
              </Row>
              {caseFilter && !isConfirmReassign && renderFilterBy()}
              {casesFiltered.length === 0 && !caseFilter && (
                <Stack direction="horizontal" gap={2} className="mt-2">
                  <p className="mb-0 me-1">{`No ${type !== "All" ? type : ""} Case Records.`}</p>
                  <a href="cases/case/create" className={"quick-link cursor-pointer"} onClick={(e: any) => newCase(e)}>
                    Create Case
                  </a>
                </Stack>
              )}
              {isReassign &&
                (isConfirmReassign ? (
                  renderConfirmReassign()
                ) : (
                  <>
                    <Row className="justify-content-center align-items-center">
                      <Col xs={1}>
                        <h6 className="app-header-text m-0">To:</h6>                          
                      </Col>
                      <Col xs={4}>
                        <SelectObjectTypeahead
                          label={caseFilter === "primaryCM" ? "Primary Case Manager" : "Secondary Case Manager"}
                          value={reassignCm}
                          options={cmUsers}
                          cssClasses="w-100"
                          onUpdate={(e: any) => updateReassignCM(e)}
                        />
                      </Col>
                      <Col xs={1}>
                        <Button
                          className="app-tertiary-color button"
                          onClick={() => setIsConfirmReassign((prev) => !prev)}
                          disabled={reassignCm && selectedReassignCases.length > 0 ? false : true}
                        >
                          Confirm
                        </Button>
                      </Col>
                    </Row>
                    <h6 className="hero-heading app-header-text mt-3">{reassignCm && `Select Cases to Reassign (${selectedReassignCases.length})`}</h6>
                    <p className={errMsg ? "errmsg" : "offscreen"} aria-live="assertive">
                      {errMsg}
                    </p>
                    <Row>
                      <Col>
                        {Object.entries(categorizeCases(casesFiltered))
                          .map(([category, items]) => renderCaseCategory(category, items as Case[]))
                          .filter(Boolean)}
                      </Col>
                    </Row>
                  </>
                )
              )}
              {isDeleting && caseToDelete ? (
                renderDelete()
              ) : !isReassign ? (
                casesFiltered?.map((item: any) => (
                  <Card key={item?.id} className={item.selected ? "border-tertiary" : "bg-light"}>
                    <Card.Body>
                      <Stack direction="horizontal" gap={3}>
                        <Stack
                          className={!caseFilter ? "cursor-pointer" : ""}
                          onClick={caseFilter ? () => onClickCaseToReassign(item) : (e) => editCase(item.id)}
                        >
                          {item.disabled_at ? (
                            <Card.Subtitle className="text-muted">{item.name}</Card.Subtitle>
                          ) : (
                            <Card.Subtitle>{item.name}</Card.Subtitle>
                          )}
                          <Card.Subtitle className="text-muted mt-1">
                            {caseFilter === "primaryCM" && item.primaryCM !== null && `Primary Case Manager`}
                            {caseFilter === "secondaryCM" && item.secondaryCM !== null && ` Secondary Case Manager`}
                          </Card.Subtitle>
                          <Card.Subtitle className="text-muted mt-1">
                            {item.primaryCM === null && filteredCm && "Primary Case Manager Unassigned"}
                          </Card.Subtitle>
                        </Stack>
                        {!caseFilter && (
                          <>
                            <Button className="app-secondary-color button" onClick={(e) => editCase(item.id)}>
                              Edit
                            </Button>
                            {!limitedAccess && (
                              <>
                                <div className="vr" />
                                <Button className="app-danger-color button" onClick={(e) => onClickDelete(item)}>
                                  Delete
                                </Button>
                              </>
                            )}
                          </>
                        )}
                      </Stack>
                    </Card.Body>
                  </Card>
                ))
              ) : null}
            </>
          )}
        </Stack>
      </Col>
    </Row>
  );
};

export default CaseTab;
